import { Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ANTRAGS_FORMULAREN, FORM_FIELDS_TYPES, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { NonRequired, Required } from 'src/lib/validation/ValidationTypes';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { mapAssoziiertePartner } from 'src/pages/private/online-antrag/application-helper-function/mapping';
import { addIndexToPath, checkReadOnlyInUeberarbeitung, getFieldNamefromDataPath } from 'src/utils/helper';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import { getTextFieldValidationByDatei } from 'src/lib/validation/fieldValidationHelper';
const P = AntragSchema.attributes;

const AssoziiertePartnerSkizzeGlobal: FC<{ hasAnyPartnerComment?: boolean }> = ({ hasAnyPartnerComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasAnyPartnerComment);

	return (
		<>
			<ApplicationHeading title='Assoziierte Partner' description={PLATZHALTER_TEXT} />

			<Typography>
				Bitte geben Sie - sofern vorhanden - die assoziierten Partner für Ihr Förderprojekt an und laden Sie den unterzeichneten Letter of Intent
				(LoI) über das Uploadfeld hoch. Eine Vorlage für einen LoI finden Sie im Download-Bereich im Menüpunkt “Allgemeine Hinweise”.
			</Typography>

			<CreateForm
				formStruc={formStruc(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};

export default AssoziiertePartnerSkizzeGlobal;

const formStruc = (application: any) => {
	const antragFormular = get(application, P.Allgemein_Antragsformular.path, '');

	switch (antragFormular) {
		case ANTRAGS_FORMULAREN.Loewe:
			return formStrucLoewe();
		default:
			return defaultFormStruc();
	}
};

const defaultFormStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Assoziierte_Partner,
					type: FORM_FIELDS_TYPES.componentTable,
					hideColumns: [
						'Partner',
						'Institutionstyp',
						'Rechtsform_Der_Organisation',
						'Relevante_Kernkompentenz',
						'Assoziierte_Partner_Strasse_Und_Hausnummer',
						'Assoziierte_Partner_Telefonnummer',
						'Assoziierte_Partner_E_Mail_Adresse',
						'LOI_Vorhanden',
						'Assoziierte_Partner_Ansprechpartnerin',
						'Pruefstatus',
						'Freigegeben_Am',
						'Ueberarbeitet_Am',
						'Erledigt_Durch_Antragsteller',
						'Hinweis_Fuer_Den_Antragsteller',
					],
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucLoewe = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Assoziierte_Partner,
					type: FORM_FIELDS_TYPES.componentTable,
					hideColumns: [
						'Partner',
						'Institutionstyp',
						'Rechtsform_Der_Organisation',
						'Relevante_Kernkompentenz',
						'Assoziierte_Partner_Strasse_Und_Hausnummer',
						'Assoziierte_Partner_Telefonnummer',
						'Assoziierte_Partner_E_Mail_Adresse',
						'LOI_Vorhanden',
						'Assoziierte_Partner_Ansprechpartnerin',
						'Pruefstatus',
						'Freigegeben_Am',
						'Ueberarbeitet_Am',
						'Erledigt_Durch_Antragsteller',
						'Hinweis_Fuer_Den_Antragsteller',
					],
					fieldProps: {
						[getFieldNamefromDataPath(P.Assoziierte_Partner.schema.attributes.Assoziierte_Partner_PLZ.path)]: {
							type: FORM_FIELDS_TYPES.integer,
							width: '100px',
						},
						[getFieldNamefromDataPath(P.Assoziierte_Partner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path)]: {
							maxWidth: '80px',
						},
						[getFieldNamefromDataPath(P.Assoziierte_Partner.schema.attributes.Assoziierte_Partner_Ort.path)]: {
							maxWidth: '70px',
						},
						[getFieldNamefromDataPath(P.Assoziierte_Partner.schema.attributes.LOI_Datei.path)]: {
							maxWidth: '90px',
						},
					},
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const customFormStruc = (application: any, i: number) => {
	const loi = get(application, addIndexToPath(P.Assoziierte_Partner.schema.attributes.LOI_Datei.path, i, P.Assoziierte_Partner.path), '');
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Assoziierte_Partner.schema.attributes.Assoziierte_Partner_PLZ, validation: [{ validateFunction: loi ? Required : NonRequired }] },
				// { ...P.Assoziierte_Partner.schema.attributes.LOI_Datei, validation: [{ validateFunction: loi ? Required : NonRequired }] },
				{ ...P.Assoziierte_Partner.schema.attributes.Assoziierte_Partner_Ort, validation: [{ validateFunction: loi ? Required : NonRequired }] },
				{
					...P.Assoziierte_Partner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation,
					validation: getTextFieldValidationByDatei(loi),
				},
			],
		},
	];
	return struc;
};

export const getFormStrucAssoziiertePartnerSkizzeGlobal = (application: any) => {
	let result: FormStrucItem[] = [];
	const antragFormular = get(application, P.Allgemein_Antragsformular.path, '');

	switch (antragFormular) {
		case ANTRAGS_FORMULAREN.Loewe:
			const assoziiertePartner = get(application, P.Assoziierte_Partner.path, []);

			assoziiertePartner.forEach((_eachPartner: any, i: number) => {
				const formStrucCT2WithIndex = mapAssoziiertePartner(customFormStruc(application, i), i);
				result = concat(result, formStrucCT2WithIndex);
			});
			break;
		default:
			return;
	}

	return result;
};
